import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import clientdocumentApiController from "../../services/clientdocument.service";
import HandleError from "../HandleError";
import HandleResponse from "../HandleResponse";
import { contentErrorMessage } from "./contentError";
import { normalizeListResponse } from "../../services/listResponse";
import { beginClientHistoryRequest, ownsClientHistoryRequest } from "./clientHistoryOwnership";

const listErrorMessage = (action, fallback = "Unable to load client documents. Please try again.") => contentErrorMessage(action, fallback);

export const clientdocumentStoreApi = createAsyncThunk("clientdocument/create", async (formvalues, thunkAPI) => {
  try {
    const resposedata = await clientdocumentApiController
      .create(formvalues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "create"))
      .catch((error) => HandleError(thunkAPI, error, "create"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue({ status: error.response.status, message: message });
  }
});

export const clientdocumentUpdateApi = createAsyncThunk("clientdocument/update", async (formvalues, thunkAPI) => {
  try {
    const resposedata = await clientdocumentApiController
      .update(formvalues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "update"))
      .catch((error) => HandleError(thunkAPI, error, "update"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue({ status: error.response.status, message: message });
  }
});

export const ClientdocumentGridViewApi = createAsyncThunk("clientdocument/gridview", async (formValues, thunkAPI) => {
  try {
    const resposedata = await clientdocumentApiController
      .view(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "gridview"))
      .catch((error) => HandleError(thunkAPI, error, "gridview"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const clientdocumentDetailApi = createAsyncThunk("clientdocument/detail", async (formValues, thunkAPI) => {
  try {
    const resposedata = await clientdocumentApiController
      .view(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "detail"))
      .catch((error) => HandleError(thunkAPI, error, "detail"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const clientdocumentDeleteApi = createAsyncThunk("clientdocument/delete", async (formValues, thunkAPI) => {
  try {
    const resposedata = await clientdocumentApiController
      .deleted(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "delete"))
      .catch((error) => HandleError(thunkAPI, error, "delete"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const clientdocumentSuggetionListApi = createAsyncThunk("clientdocument/suggetionlist", async (formValues, thunkAPI) => {
  try {
    const resposedata = await clientdocumentApiController
      .suggetionlist(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "suggetionlist"))
      .catch((error) => HandleError(thunkAPI, error, "suggetionlist"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

const initialState = {
  listClientId: null,
  listQueryKey: null,
  listRequestId: null,
  isGridView: [],
  isGridViewError: "",
  isDocumentDrawer: "",
  isApiStatus: {
    clientdocumentStoreApi: "",
    clientdocumentUpdateApi: "",
    ClientdocumentGridViewApi: "",
    clientdocumentDeleteApi: "",
  },
};

const clientdocumentSlice = createSlice({
  name: "clientdocument",
  initialState,
  reducers: {
    reset: () => initialState,
    openDocumentDrawer: (state) => {
      state.isDocumentDrawer = "open";
    },
    closeDocumentDrawer: (state) => {
      state.isDocumentDrawer = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(clientdocumentStoreApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, clientdocumentStoreApi: "loading" };
      })
      .addCase(clientdocumentStoreApi.fulfilled, (state) => {
        state.isApiStatus = { ...state.isApiStatus, clientdocumentStoreApi: "succeeded" };
      })
      .addCase(clientdocumentStoreApi.rejected, (state) => {
        state.isApiStatus = { ...state.isApiStatus, clientdocumentStoreApi: "failed" };
      })
      .addCase(clientdocumentUpdateApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, clientdocumentUpdateApi: "loading" };
      })
      .addCase(clientdocumentUpdateApi.fulfilled, (state, action) => {
        const { id, ...changes } = action.payload;
        let isGridView = state.isGridView && state.isGridView.data ? state.isGridView.data : state.isGridView;
        const existingGridData = isGridView ? isGridView.find((event) => event.id === id) : "";
        if (existingGridData) {
          Object.keys(changes).map((keyName) => {
            existingGridData[keyName] = changes[keyName];
          });
        }
      })
      .addCase(clientdocumentUpdateApi.rejected, (state) => {
        state.isApiStatus = { ...state.isApiStatus, clientdocumentUpdateApi: "failed" };
      })
      .addCase(ClientdocumentGridViewApi.pending, (state, action) => {
        // The shared helper tracks request ownership and clears its canonical
        // isListView; this grid slice renders isGridView, so clear that too.
        const previousQueryKey = state.listQueryKey;
        const clientChanged = beginClientHistoryRequest(state, action);
        if (clientChanged || state.listQueryKey !== previousQueryKey) state.isGridView = [];
        state.isApiStatus = { ...state.isApiStatus, ClientdocumentGridViewApi: "loading" };
        state.isGridViewError = "";
      })
      .addCase(ClientdocumentGridViewApi.fulfilled, (state, action) => {
        if (!ownsClientHistoryRequest(state, action)) return;
        const payload = normalizeListResponse(action.payload);
        let old_current_page = state.isGridView.current_page ? state.isGridView.current_page : "";
        let new_current_page = payload.current_page ? payload.current_page : "";
        let viewdata = state.isGridView && state.isGridView.data;
        let newviewdata = payload && payload.data;
        state.isGridView = payload;
        if (old_current_page && new_current_page && old_current_page < new_current_page && old_current_page != new_current_page) {
          viewdata && newviewdata ? (state.isGridView.data = [...viewdata, ...newviewdata]) : payload;
        }
        state.isApiStatus = { ...state.isApiStatus, ClientdocumentGridViewApi: "succeeded" };
        state.isGridViewError = "";
      })
      .addCase(ClientdocumentGridViewApi.rejected, (state, action) => {
        if (!ownsClientHistoryRequest(state, action)) return;
        state.isApiStatus = { ...state.isApiStatus, ClientdocumentGridViewApi: "failed" };
        state.isGridViewError = listErrorMessage(action);
      })
      .addCase(clientdocumentDeleteApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, clientdocumentDeleteApi: "loading" };
      })
      .addCase(clientdocumentDeleteApi.fulfilled, (state, action) => {
        const { id } = action.payload;
        state.isGridView.data = state.isGridView.data ? state.isGridView.data.filter((item) => item.id != id) : state.isGridView.filter((item) => item.id != id);
      });
  },
});
// Action creators are generated for each case reducer function
export const { reset, openDocumentDrawer, closeDocumentDrawer } = clientdocumentSlice.actions;
export default clientdocumentSlice.reducer;
